import { fetchEventSource } from "@microsoft/fetch-event-source";
import {
    CHAT_MESSAGE_TYPE,
    COPILOT_CONTENT_MARKDOWN_ARG_KEY,
    COPILOT_INTERACTION_TOOL_NAMES,
    COPILOT_MESSAGE_TYPE,
    CopilotChatRoles,
    CopilotMessageStateOptions,
    type CopilotAttachmentUploadResult,
    type CopilotSSEEvent,
    CopilotSSEEventTypes,
    type GenerateReplyRequest,
    type CopilotChatMessageBlock,
    type CopilotChatMessageContents,
    type CopilotToolCallMessageContents,
    type CopilotAgentSummaryMessageContents,
    type CopilotAgentBuilderTriggerMessageContents,
    type CopilotSubAgentPanel,
    type CopilotDisplayMessage,
    type ToolCallEvent,
    type DelegationEventFields,
} from "@regal-voice/shared-types";
import { nanoid } from "nanoid";

import { COPILOT_SSE_CANCEL_ACTION } from "Pages/copilot/copilotSseConstants";
import { isCopilotDelegationToolName } from "Pages/copilot/Pages/CopilotEditorPage/utils";
import { getCopilotGenerateReplyUrl, getCopilotSseHeaders } from "Services/CopilotApiService";
import { getLogger } from "Services/LoggingService";

const logger = getLogger("runCopilotGenerateReplyStream", { regal_copilot: true });

export type RunCopilotStreamCallbacks = {
    onComplete?: () => void | Promise<void>;
    onSessionTitle?: (title: string) => void;
    onWorkflowComplete?: (workflow?: string) => void;
    onError?: (error: Error) => void;
};

export type RunCopilotGenerateReplyStreamArgs = RunCopilotStreamCallbacks & {
    signal: AbortSignal;
    sessionId: string;
    systemPrompt: string;
    userMessageText: string;
    /** Uploaded attachment metadata; drives both the request `filenames` and the optimistic user message. */
    attachmentUploads?: CopilotAttachmentUploadResult[];
    updateMessages: (updater: (prev: CopilotDisplayMessage[]) => CopilotDisplayMessage[]) => void;
    /** Fires after the HTTP response is OK and the SSE reader is ready (avoids aborting during handshake). */
    onSseOpened?: () => void;
};

function isCopilotToolCallMessage(msg: CopilotDisplayMessage): msg is CopilotToolCallMessageContents {
    return msg.messageType === CHAT_MESSAGE_TYPE.FUNCTION_CALL;
}

function getDelegationFields(event: CopilotSSEEvent): DelegationEventFields {
    const raw = event as CopilotSSEEvent & DelegationEventFields;
    return {
        agentId: typeof raw.agentId === "string" ? raw.agentId : undefined,
        parentCallId: typeof raw.parentCallId === "string" ? raw.parentCallId : undefined,
        delegationTaskId: typeof raw.delegationTaskId === "string" ? raw.delegationTaskId : undefined,
    };
}

/** Accumulated state for a single delegated sub-agent (one `delegationTaskId`). */
type DelegationSubAgentState = {
    content: string;
    toolCallCount: number;
};

/** Accumulated state for one `delegate_tasks` call and all of the sub-agents it spawned. */
type DelegationContainerState = {
    messageId: string;
    callId: string;
    name: string;
    startedAtMs?: number;
    /** Sub-agent task ids in first-seen order, used to keep panel ordering stable. */
    subAgentOrder: string[];
    subAgents: Map<string, DelegationSubAgentState>;
    finished: boolean;
};

export type CopilotStreamReducerArgs = {
    updateMessages: (updater: (prev: CopilotDisplayMessage[]) => CopilotDisplayMessage[]) => void;
    onSessionTitle?: (title: string) => void;
    onWorkflowComplete?: (workflow?: string) => void;
    onError?: (error: Error) => void;
};

export type CopilotStreamReducer = {
    /** Apply one parsed SSE event to the message list. */
    handleEvent: (event: CopilotSSEEvent) => void;
    /** Flush any in-progress thinking/assistant message to its FINISHED state. */
    finalize: () => void;
    /** Replace the in-progress message with a generic streaming-error message. */
    fail: (error: Error) => void;
};

/**
 * Builds the stateful reducer that turns a sequence of Copilot SSE events into the live
 * message list. Shared by the real network stream ({@link runCopilotGenerateReplyStream})
 * and the local mock replay ({@link runCopilotMockEventReplayStream}).
 */
export function createCopilotStreamReducer({
    updateMessages,
    onSessionTitle,
    onWorkflowComplete,
    onError,
}: CopilotStreamReducerArgs): CopilotStreamReducer {
    let currentMessage = createAgentMessage(nanoid(), "", CopilotMessageStateOptions.THINKING);
    const toolCallMessages = new Map<string, CopilotToolCallMessageContents>();
    let hasAddedCurrentMessage = false;
    let nativeThinkingMessageId: string | null = null;

    /**
     * Sub-agent delegation containers keyed by the spawning `delegate_tasks` tool-call id.
     * The events emitted by each delegated sub-agent carry that id as their `parentCallId`,
     * and a `delegationTaskId` identifying the individual sub-agent. We fold their thinking
     * and tool calls into a single sub-agent thinking container so the live stream renders
     * identically to a persisted delegation session.
     */
    const delegationContainers = new Map<string, DelegationContainerState>();

    const upsertMessageById = (message: CopilotDisplayMessage) => {
        updateMessages((prev) => {
            const existingIndex = prev.findIndex((msg) => msg.id === message.id);

            if (existingIndex !== -1) {
                const next = [...prev];
                next[existingIndex] = message;
                return next;
            }

            return [...prev, message];
        });
    };

    const upsertCurrentMessage = () => {
        if (!hasAddedCurrentMessage) {
            // Don't materialize an empty assistant bubble. A leading message_state event (or any
            // non-text event) would otherwise insert it before the thinking block / tool calls
            // that arrive next, pushing the eventual text above them. Wait until it has content
            // so it lands in the correct position relative to the surrounding messages.
            if (!currentMessage.text) {
                return;
            }
            hasAddedCurrentMessage = true;
            updateMessages((prev) => [...prev, currentMessage]);
            return;
        }

        upsertMessageById(currentMessage);
    };

    const startNewAssistantMessage = () => {
        currentMessage = createAgentMessage(nanoid(), "", CopilotMessageStateOptions.THINKING);
        hasAddedCurrentMessage = false;
    };

    const finishNativeThinkingIfNeeded = () => {
        if (!nativeThinkingMessageId) {
            return;
        }
        const id = nativeThinkingMessageId;
        nativeThinkingMessageId = null;
        updateMessages((prev) => {
            const idx = prev.findIndex((msg) => msg.id === id);
            if (idx === -1) {
                return prev;
            }
            const msg = prev[idx];
            if (!isCopilotToolCallMessage(msg) || msg.name !== COPILOT_INTERACTION_TOOL_NAMES.NATIVE_LLM_THINKING) {
                return prev;
            }
            const next = [...prev];
            next[idx] = { ...msg, responseState: CopilotMessageStateOptions.FINISHED };
            return next;
        });
    };

    const appendThinkingDelta = (delta: string) => {
        if (!delta) {
            return;
        }
        if (!nativeThinkingMessageId) {
            const id = nanoid();
            nativeThinkingMessageId = id;
            const toolMsg: CopilotToolCallMessageContents = {
                id,
                isAgent: true,
                timestamp: Date.now(),
                messageType: CHAT_MESSAGE_TYPE.FUNCTION_CALL,
                callId: id,
                name: COPILOT_INTERACTION_TOOL_NAMES.NATIVE_LLM_THINKING,
                args: { [COPILOT_CONTENT_MARKDOWN_ARG_KEY]: delta },
                responseState: CopilotMessageStateOptions.THINKING,
            };
            updateMessages((prev) => [...prev, toolMsg]);
            return;
        }
        updateMessages((prev) => {
            const idx = prev.findIndex((msg) => msg.id === nativeThinkingMessageId);
            if (idx === -1) {
                return prev;
            }
            const msg = prev[idx];
            if (!isCopilotToolCallMessage(msg) || msg.name !== COPILOT_INTERACTION_TOOL_NAMES.NATIVE_LLM_THINKING) {
                return prev;
            }
            const prevMd =
                typeof msg.args[COPILOT_CONTENT_MARKDOWN_ARG_KEY] === "string"
                    ? msg.args[COPILOT_CONTENT_MARKDOWN_ARG_KEY]
                    : "";
            const next = [...prev];
            next[idx] = {
                ...msg,
                args: { ...msg.args, [COPILOT_CONTENT_MARKDOWN_ARG_KEY]: prevMd + delta },
            };
            return next;
        });
    };

    const completeCurrentMessageIfNeeded = () => {
        if (!hasAddedCurrentMessage || currentMessage.responseState === CopilotMessageStateOptions.FINISHED) {
            return;
        }

        currentMessage = {
            ...currentMessage,
            responseState: CopilotMessageStateOptions.FINISHED,
            contentBlocks: currentMessage.contentBlocks?.map((block) => ({
                ...block,
                state: CopilotMessageStateOptions.FINISHED,
            })),
        };

        upsertCurrentMessage();
    };

    const renderDelegationContainer = (state: DelegationContainerState) => {
        const subAgents: CopilotSubAgentPanel[] = state.subAgentOrder.map((taskId) => {
            const subAgent = state.subAgents.get(taskId);
            const count = subAgent?.toolCallCount ?? 0;
            return {
                id: taskId,
                name: `Executor — ${count} task${count === 1 ? "" : "s"}`,
                contentMarkdown: subAgent?.content ?? "",
            };
        });

        const totalToolCalls = state.subAgentOrder.reduce(
            (sum, taskId) => sum + (state.subAgents.get(taskId)?.toolCallCount ?? 0),
            0
        );
        const summary =
            `Delegated ${totalToolCalls} task${totalToolCalls === 1 ? "" : "s"} to the executor across ` +
            `${subAgents.length} parallel sub-agent${subAgents.length === 1 ? "" : "s"}.`;

        const message: CopilotToolCallMessageContents = {
            id: state.messageId,
            isAgent: true,
            timestamp: state.startedAtMs ?? Date.now(),
            messageType: CHAT_MESSAGE_TYPE.FUNCTION_CALL,
            callId: state.callId,
            name: state.name,
            args: { [COPILOT_CONTENT_MARKDOWN_ARG_KEY]: summary },
            subAgents,
            responseState: state.finished ? CopilotMessageStateOptions.FINISHED : CopilotMessageStateOptions.THINKING,
            startedAtMs: state.startedAtMs,
        };
        upsertMessageById(message);
    };

    const ensureDelegationSubAgent = (state: DelegationContainerState, taskId: string): DelegationSubAgentState => {
        let subAgent = state.subAgents.get(taskId);
        if (!subAgent) {
            subAgent = { content: "", toolCallCount: 0 };
            state.subAgents.set(taskId, subAgent);
            state.subAgentOrder.push(taskId);
        }
        return subAgent;
    };

    const startDelegationContainer = (event: ToolCallEvent) => {
        // A delegate_tasks call is a boundary for the main agent, just like any other tool call.
        finishNativeThinkingIfNeeded();
        completeCurrentMessageIfNeeded();
        startNewAssistantMessage();

        const state: DelegationContainerState = {
            messageId: nanoid(),
            callId: event.id,
            name: event.name,
            startedAtMs: event.createdAtMs,
            subAgentOrder: [],
            subAgents: new Map(),
            finished: false,
        };
        delegationContainers.set(event.id, state);
        renderDelegationContainer(state);
    };

    const appendDelegatedThinking = (parentCallId: string, delegationTaskId: string | undefined, content: string) => {
        if (!content) {
            return;
        }
        const state = delegationContainers.get(parentCallId);
        if (!state) {
            return;
        }
        const subAgent = ensureDelegationSubAgent(state, delegationTaskId ?? parentCallId);
        subAgent.content += content;
        renderDelegationContainer(state);
    };

    const handleDelegatedToolCall = (
        parentCallId: string,
        delegationTaskId: string | undefined,
        event: ToolCallEvent
    ) => {
        const state = delegationContainers.get(parentCallId);
        if (!state) {
            return;
        }
        const taskId = delegationTaskId ?? parentCallId;
        const subAgent = ensureDelegationSubAgent(state, taskId);
        subAgent.toolCallCount += 1;

        // parentId links this tool call to its sub-agent panel; buildRenderItems folds it in.
        const toolMsg: CopilotToolCallMessageContents = {
            id: nanoid(),
            isAgent: true,
            timestamp: Date.now(),
            messageType: CHAT_MESSAGE_TYPE.FUNCTION_CALL,
            callId: event.id,
            name: event.name,
            args: event.args,
            parentId: taskId,
            responseState: CopilotMessageStateOptions.THINKING,
            startedAtMs: event.createdAtMs,
        };
        toolCallMessages.set(event.id, toolMsg);
        updateMessages((prev) => [...prev, toolMsg]);
        renderDelegationContainer(state);
    };

    const finalizeDelegationContainer = (callId: string) => {
        const state = delegationContainers.get(callId);
        if (!state) {
            return;
        }
        state.finished = true;
        renderDelegationContainer(state);
    };

    const handleToolCallEvent = (event: {
        id: string;
        name: string;
        args: Record<string, unknown>;
        parentId?: string;
        createdAtMs?: number;
    }) => {
        // Tool calls act as boundaries: finish any native thinking block, then start a fresh assistant message.
        finishNativeThinkingIfNeeded();
        completeCurrentMessageIfNeeded();
        startNewAssistantMessage();
        const toolMsg: CopilotToolCallMessageContents = {
            id: nanoid(),
            isAgent: true,
            timestamp: Date.now(),
            messageType: CHAT_MESSAGE_TYPE.FUNCTION_CALL,
            callId: event.id,
            name: event.name,
            args: event.args,
            parentId: event.parentId,
            responseState: CopilotMessageStateOptions.THINKING,
            startedAtMs: event.createdAtMs,
        };
        toolCallMessages.set(event.id, toolMsg);
        updateMessages((prev) => [...prev, toolMsg]);
    };

    const handleToolResultEvent = (event: {
        id: string;
        result: unknown;
        structuredResult?: Record<string, unknown>;
        isError?: boolean;
        createdAtMs?: number;
    }) => {
        const existing = toolCallMessages.get(event.id);
        if (existing) {
            const finishedAtMs = event.createdAtMs;
            const durationMs =
                finishedAtMs !== undefined && existing.startedAtMs !== undefined
                    ? finishedAtMs - existing.startedAtMs
                    : undefined;

            const updated: CopilotToolCallMessageContents = {
                ...existing,
                result: normalizeToolResult(event.result),
                structuredResult: event.structuredResult,
                isError: event.isError ?? false,
                responseState: CopilotMessageStateOptions.FINISHED,
                finishedAtMs,
                durationMs,
            };
            toolCallMessages.set(event.id, updated);
            upsertMessageById(updated);
            return;
        }

        logger.warn("Received tool_result event with no matching tool_call", {
            callId: event.id,
            result: event.result,
            isError: event.isError,
        });
    };

    const handleStreamingEvent = (event: CopilotSSEEvent) => {
        currentMessage = applyStreamingEvent(currentMessage, event);
        upsertCurrentMessage();

        if (event.type === CopilotSSEEventTypes.ERROR) {
            onError?.(new Error(event.message));
        }
    };

    const handleStreamError = (error: Error) => {
        logger.error("Copilot SSE stream error", { error });

        // Finalize any in-progress message already in the array before replacing currentMessage.
        // Without this, the old message stays stuck at "streaming" / "thinking" in the list.
        finishNativeThinkingIfNeeded();
        completeCurrentMessageIfNeeded();

        currentMessage = createAgentMessage(
            nanoid(),
            "There was an issue streaming the response.",
            CopilotMessageStateOptions.FINISHED
        );
        upsertCurrentMessage();
        onError?.(error);
    };

    const handleEvent = (event: CopilotSSEEvent) => {
        const { parentCallId, delegationTaskId } = getDelegationFields(event);

        // A delegate_tasks call spawns sub-agents: render it as a sub-agent thinking container
        // rather than a flat tool card, and fold the sub-agents' events into it.
        if (event.type === CopilotSSEEventTypes.TOOL_CALL && isCopilotDelegationToolName(event.name)) {
            startDelegationContainer(event);
            return;
        }

        // The delegate_tasks tool result finalizes the container. Its aggregated output is not
        // rendered as a separate card (the sub-agent panels carry the detail).
        if (event.type === CopilotSSEEventTypes.TOOL_RESULT && delegationContainers.has(event.id)) {
            finalizeDelegationContainer(event.id);
            return;
        }

        // Events emitted by a delegated sub-agent carry the delegate_tasks call id as parentCallId.
        // Route them into the matching container instead of the main assistant message.
        if (parentCallId && delegationContainers.has(parentCallId)) {
            if (event.type === CopilotSSEEventTypes.THINKING_DELTA) {
                appendDelegatedThinking(parentCallId, delegationTaskId, event.content);
                return;
            }
            if (event.type === CopilotSSEEventTypes.TOOL_CALL) {
                handleDelegatedToolCall(parentCallId, delegationTaskId, event);
                return;
            }
            if (event.type === CopilotSSEEventTypes.TOOL_RESULT) {
                handleToolResultEvent({
                    id: event.id,
                    result: event.result,
                    structuredResult: event.structuredResult,
                    isError: event.isError,
                    createdAtMs: event.createdAtMs,
                });
                return;
            }
            // message_state and other sub-agent events do not affect the main assistant message.
            return;
        }

        if (event.type === CopilotSSEEventTypes.THINKING_DELTA) {
            appendThinkingDelta(event.content);
            return;
        }

        // MESSAGE_STATE: STREAMING signals that the model has finished its internal reasoning
        // and is about to start emitting text tokens. Close the native thinking block here so
        // it transitions to FINISHED before the first TEXT_DELTA arrives, rather than waiting
        // for the TEXT_DELTA handler to close it one event later.
        if (event.type === CopilotSSEEventTypes.MESSAGE_STATE && event.state === CopilotMessageStateOptions.STREAMING) {
            finishNativeThinkingIfNeeded();
        }

        if (event.type === CopilotSSEEventTypes.TOOL_CALL) {
            handleToolCallEvent({
                id: event.id,
                name: event.name,
                args: event.args,
                parentId: event.parentId,
                createdAtMs: event.createdAtMs,
            });
            return;
        }

        if (event.type === CopilotSSEEventTypes.TOOL_RESULT) {
            handleToolResultEvent({
                id: event.id,
                result: event.result,
                structuredResult: event.structuredResult,
                isError: event.isError,
                createdAtMs: event.createdAtMs,
            });
            return;
        }

        if (event.type === CopilotSSEEventTypes.TEXT_DELTA) {
            finishNativeThinkingIfNeeded();
        }

        if (event.type === CopilotSSEEventTypes.WORKFLOW_COMPLETE) {
            onWorkflowComplete?.(event.workflow);
        }

        if (event.type === CopilotSSEEventTypes.DONE && event.sessionTitle) {
            onSessionTitle?.(event.sessionTitle);
        }

        if (event.type === CopilotSSEEventTypes.AGENT_SUMMARY) {
            const summaryMsg: CopilotAgentSummaryMessageContents = {
                id: nanoid(),
                isAgent: true,
                timestamp: event.createdAtMs ?? Date.now(),
                messageType: COPILOT_MESSAGE_TYPE.AGENT_SUMMARY,
                title: event.title,
                contentMarkdown: event.content,
                responseState: CopilotMessageStateOptions.FINISHED,
            };
            updateMessages((prev) => [...prev, summaryMsg]);
            return;
        }

        if (event.type === CopilotSSEEventTypes.AGENT_BUILDER_TRIGGER) {
            const triggerMsg: CopilotAgentBuilderTriggerMessageContents = {
                id: nanoid(),
                isAgent: true,
                timestamp: event.createdAtMs ?? Date.now(),
                messageType: COPILOT_MESSAGE_TYPE.AB_TRIGGER,
                agentId: event.agentId,
                agentName: event.agentName,
                version: event.version,
                variantId: event.variantId,
                channel: event.channel,
                responseState: CopilotMessageStateOptions.FINISHED,
            };
            updateMessages((prev) => [...prev, triggerMsg]);
            return;
        }

        handleStreamingEvent(event);
    };

    return {
        handleEvent,
        finalize: () => {
            finishNativeThinkingIfNeeded();
            completeCurrentMessageIfNeeded();
        },
        fail: handleStreamError,
    };
}

/**
 * Runs a single Copilot generate-reply SSE stream using the pydantic streaming protocol.
 * Caller owns {@link AbortSignal} (e.g. per-session AbortController).
 */
export async function runCopilotGenerateReplyStream({
    signal,
    sessionId,
    systemPrompt,
    userMessageText,
    attachmentUploads = [],
    updateMessages,
    onComplete,
    onSessionTitle,
    onWorkflowComplete,
    onError,
    onSseOpened,
}: RunCopilotGenerateReplyStreamArgs): Promise<void> {
    const userMessage = createUserMessage(userMessageText, attachmentUploads);
    updateMessages((prev) => [...prev, userMessage]);

    const { handleEvent, finalize, fail } = createCopilotStreamReducer({
        updateMessages,
        onSessionTitle,
        onWorkflowComplete,
        onError,
    });

    const requestBody: GenerateReplyRequest = {
        sessionId,
        systemPrompt,
        userMessage: userMessageText,
        maxIterations: 40,
        ...(attachmentUploads.length > 0
            ? { filenames: attachmentUploads.map((attachment) => attachment.filename) }
            : {}),
    };

    let didComplete = false;
    let didError = false;
    let doneSeen = false;
    let eventsReceived = 0;
    let lastEventType: string | undefined;
    let sseCancelActionEmitted = false;
    const streamingStartedAtMs = Date.now();

    const emitSseCancelAction = (
        reason: "sse_open_failed" | "sse_error_before_done",
        extra: Record<string, unknown>
    ) => {
        if (sseCancelActionEmitted) {
            return;
        }
        sseCancelActionEmitted = true;
        logger.action(COPILOT_SSE_CANCEL_ACTION, {
            reason,
            sessionId,
            streamingProtocol: "pydantic",
            streamingMs: Date.now() - streamingStartedAtMs,
            eventsReceived,
            lastEventType,
            ...extra,
        });
    };

    const completeOnce = async () => {
        if (!didComplete) {
            didComplete = true;
            finalize();
            await Promise.resolve(onComplete?.());
        }
    };

    const errorOnce = (error: Error) => {
        if (!didError) {
            didError = true;
            fail(error);
        }
    };

    try {
        await fetchEventSource(getCopilotGenerateReplyUrl(), {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                ...getCopilotSseHeaders(),
            },
            body: JSON.stringify(requestBody),
            credentials: "include",
            openWhenHidden: true,
            signal,
            onopen: async (response) => {
                if (!response.ok) {
                    emitSseCancelAction("sse_open_failed", {
                        httpStatus: response.status,
                        httpStatusText: response.statusText,
                    });
                    throw new Error(`HTTP ${response.status}: ${response.statusText}`);
                }
                logger.log("SSE connection opened");
                onSseOpened?.();
            },
            onmessage: (event) => {
                const parsedEvent = parseCopilotEvent(event.data);
                if (parsedEvent) {
                    eventsReceived += 1;
                    lastEventType = parsedEvent.type;
                    if (parsedEvent.type === CopilotSSEEventTypes.DONE) {
                        doneSeen = true;
                    }
                    handleEvent(parsedEvent);
                }
            },
            onclose: () => {
                logger.log("SSE connection closed");
                void completeOnce();
            },
            onerror: (error) => {
                const streamError = error instanceof Error ? error : new Error("SSE stream error");
                // ``signal.aborted`` means a caller invoked ``AbortController.abort()``,
                // which has already emitted its own ``copilot.sse.cancel`` action. Only
                // log here for the genuine upstream-error case (network drop, ALB / 5xx)
                // where the connection ended before we saw a ``done`` event.
                if (!signal.aborted && !doneSeen) {
                    emitSseCancelAction("sse_error_before_done", {
                        errorName: streamError.name,
                        errorMessage: streamError.message,
                    });
                }
                logger.error("SSE error", { error: streamError });
                errorOnce(streamError);
                throw streamError;
            },
        });
    } catch (error) {
        const streamError = error instanceof Error ? error : new Error("SSE stream failed");
        if (streamError.name !== "AbortError") {
            errorOnce(streamError);
        }
    } finally {
        await completeOnce();
    }
}

function createUserMessage(text: string, attachments: CopilotAttachmentUploadResult[]): CopilotChatMessageContents {
    const id = nanoid();
    const createdAt = new Date();

    return {
        id,
        text,
        isAgent: false,
        timestamp: createdAt.getTime(),
        messageType: CHAT_MESSAGE_TYPE.MESSAGE,
        responseState: CopilotMessageStateOptions.FINISHED,
        contentBlocks: [
            {
                id,
                role: CopilotChatRoles.USER,
                state: CopilotMessageStateOptions.FINISHED,
                content: text,
                createdAt,
                ...(attachments.length > 0 ? { attachments } : {}),
            },
        ],
    };
}

function applyStreamingEvent(message: CopilotChatMessageContents, event: CopilotSSEEvent): CopilotChatMessageContents {
    const currentBlock = message.contentBlocks?.[0];
    if (!currentBlock) {
        return message;
    }

    switch (event.type) {
        case CopilotSSEEventTypes.ERROR:
            return {
                ...message,
                text: event.message,
                responseState: CopilotMessageStateOptions.FINISHED,
                contentBlocks: [
                    {
                        ...currentBlock,
                        state: CopilotMessageStateOptions.FINISHED,
                        content: event.message,
                    },
                ],
            };
        case CopilotSSEEventTypes.TEXT_DELTA:
            return {
                ...message,
                text: message.text + event.content,
                responseState: CopilotMessageStateOptions.STREAMING,
                contentBlocks: [
                    {
                        ...currentBlock,
                        state: CopilotMessageStateOptions.STREAMING,
                        content: currentBlock.content + event.content,
                    },
                ],
            };
        case CopilotSSEEventTypes.DONE:
            return {
                ...message,
                responseState: CopilotMessageStateOptions.FINISHED,
                contentBlocks: [
                    {
                        ...currentBlock,
                        state: CopilotMessageStateOptions.FINISHED,
                    },
                ],
            };
        default:
            return message;
    }
}

function createAgentMessage(
    id: string,
    content: string,
    state: CopilotChatMessageBlock["state"]
): CopilotChatMessageContents {
    const timestamp = Date.now();

    return {
        id,
        text: content,
        isAgent: true,
        timestamp,
        messageType: CHAT_MESSAGE_TYPE.MESSAGE,
        responseState: state,
        contentBlocks: [
            {
                id,
                role: CopilotChatRoles.ASSISTANT,
                content,
                createdAt: new Date(timestamp),
                state,
            },
        ],
    };
}

function normalizeToolResult(result: unknown): Record<string, unknown> | string {
    if (typeof result === "string") {
        return result;
    }

    if (result && typeof result === "object") {
        return result as Record<string, unknown>;
    }

    return JSON.stringify(result);
}

function parseCopilotEvent(data: string): CopilotSSEEvent | null {
    try {
        const parsed = JSON.parse(data) as Partial<CopilotSSEEvent>;

        switch (parsed.type) {
            case CopilotSSEEventTypes.TEXT_DELTA:
            case CopilotSSEEventTypes.THINKING_DELTA:
                if (typeof parsed.content !== "string") {
                    return null;
                }
                break;
            case CopilotSSEEventTypes.TOOL_CALL:
                if (
                    typeof (parsed as Partial<{ name: unknown }>).name !== "string" ||
                    typeof (parsed as Partial<{ args: unknown }>).args !== "object" ||
                    (parsed as Partial<{ args: unknown }>).args === null ||
                    typeof (parsed as Partial<{ id: unknown }>).id !== "string"
                ) {
                    return null;
                }
                break;
            case CopilotSSEEventTypes.TOOL_RESULT:
                if (
                    typeof (parsed as Partial<{ name: unknown }>).name !== "string" ||
                    typeof (parsed as Partial<{ id: unknown }>).id !== "string" ||
                    (parsed as Partial<{ result: unknown }>).result === undefined
                ) {
                    return null;
                }
                break;
            case CopilotSSEEventTypes.ERROR:
                if (typeof (parsed as Partial<{ message: unknown }>).message !== "string") {
                    return null;
                }
                break;
            case CopilotSSEEventTypes.MESSAGE_STATE:
            case CopilotSSEEventTypes.WORKFLOW_COMPLETE:
            case CopilotSSEEventTypes.DONE:
                // No required fields beyond type
                break;
            case CopilotSSEEventTypes.AGENT_SUMMARY:
                if (
                    typeof (parsed as Partial<{ title: unknown }>).title !== "string" ||
                    typeof (parsed as Partial<{ content: unknown }>).content !== "string"
                ) {
                    return null;
                }
                break;
            case CopilotSSEEventTypes.AGENT_BUILDER_TRIGGER:
                if (
                    typeof (parsed as Partial<{ agentId: unknown }>).agentId !== "string" ||
                    typeof (parsed as Partial<{ agentName: unknown }>).agentName !== "string" ||
                    !Number.isInteger((parsed as Partial<{ version: unknown }>).version)
                ) {
                    return null;
                }
                break;
            default:
                return null;
        }

        return parsed as CopilotSSEEvent;
    } catch {
        return null;
    }
}
